import React from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { MainTabParamList } from './types';
import { ResponsiveLayout } from '@/layouts/ResponsiveLayout';
import { useResponsive } from '@/hooks/useResponsive';
import { TabNavigationProvider } from './TabNavigationProvider';

// Import navigators and screens
import { HomeNavigator } from './HomeNavigator';
import { GroupsNavigator } from './GroupsNavigator';
import { WatchlistScreen } from '@/screens/watchlist/WatchlistScreen';
import { ResearchNavigator } from './ResearchNavigator';
import { FeedbackScreen } from '@/screens/feedback/FeedbackScreen';
import { ProfileScreen } from '@/screens/profile/ProfileScreen';

const Tab = createBottomTabNavigator<MainTabParamList>();

// Feature flag: Set to true to enable desktop sidebar layout
const ENABLE_DESKTOP_SIDEBAR = true;

/**
 * Tab Content Wrapper
 * Provides navigation context and responsive layout from INSIDE the Tab.Navigator
 */
const TabContentWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <TabNavigationProvider>
      <ResponsiveLayout enableDesktopLayout={ENABLE_DESKTOP_SIDEBAR}>{children}</ResponsiveLayout>
    </TabNavigationProvider>
  );
};

/**
 * Main Tab Navigator
 * Bottom tabs for authenticated users with desktop sidebar support
 */
export const MainNavigator: React.FC = () => {
  const { isDesktop, isMobile, isTablet } = useResponsive();
  const shouldHideBottomTabs = isDesktop && ENABLE_DESKTOP_SIDEBAR;

  // Responsive tab bar height based on device type
  const tabBarHeight = isMobile ? 72 : isTablet ? 72 : 60;
  const tabBarPaddingBottom = isMobile ? 10 : isTablet ? 12 : 8;
  const tabBarPaddingTop = 8;

  return (
    <Tab.Navigator
      screenOptions={{
        headerShown: false,
        tabBarStyle: shouldHideBottomTabs
          ? {
              display: 'none' as any,
            }
          : {
              backgroundColor: Colors.background.secondary,
              borderTopColor: Colors.border.subtle,
              borderTopWidth: 1,
              height: tabBarHeight,
              paddingBottom: tabBarPaddingBottom,
              paddingTop: tabBarPaddingTop,
            },
        tabBarActiveTintColor: Colors.accent.primary,
        tabBarInactiveTintColor: Colors.text.tertiary,
        tabBarLabelStyle: {
          fontSize: 12,
          fontWeight: '600',
        },
      }}
    >
      <Tab.Screen
        name="Home"
        options={{
          tabBarIcon: ({ color, size }) => <Ionicons name="home" size={size} color={color} />,
        }}
      >
        {() => (
          <TabContentWrapper>
            <HomeNavigator />
          </TabContentWrapper>
        )}
      </Tab.Screen>

      <Tab.Screen
        name="Research"
        options={{
          tabBarIcon: ({ color, size }) => <Ionicons name="flask" size={size} color={color} />,
        }}
      >
        {() => (
          <TabContentWrapper>
            <ResearchNavigator />
          </TabContentWrapper>
        )}
      </Tab.Screen>

      <Tab.Screen
        name="Watchlist"
        options={{
          tabBarIcon: ({ color, size }) => <Ionicons name="star" size={size} color={color} />,
        }}
      >
        {() => (
          <TabContentWrapper>
            <WatchlistScreen />
          </TabContentWrapper>
        )}
      </Tab.Screen>

      <Tab.Screen
        name="Groups"
        options={{
          tabBarIcon: ({ color, size }) => (
            <Ionicons name="chatbubbles" size={size} color={color} />
          ),
        }}
      >
        {() => (
          <TabContentWrapper>
            <GroupsNavigator />
          </TabContentWrapper>
        )}
      </Tab.Screen>

      <Tab.Screen
        name="Feedback"
        options={{
          tabBarIcon: ({ color, size }) => <Ionicons name="megaphone" size={size} color={color} />,
        }}
      >
        {() => (
          <TabContentWrapper>
            <FeedbackScreen />
          </TabContentWrapper>
        )}
      </Tab.Screen>

      <Tab.Screen
        name="Profile"
        options={{
          tabBarIcon: ({ color, size }) => <Ionicons name="person" size={size} color={color} />,
        }}
      >
        {() => (
          <TabContentWrapper>
            <ProfileScreen />
          </TabContentWrapper>
        )}
      </Tab.Screen>
    </Tab.Navigator>
  );
};
